import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { relayEvent, takePendingUtm } from '../utils/trackerLoader';

const API_URL = 'https://joli-mot.fr/api-2026.php';
const CLE_API = '34KVHFQSP226YTk4n256QP6rGbqhA5w2ymY7W3pLwdbuc3i75';

// Génération d'un ID de session unique
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('tracking_session_id');
  if (!sessionId) {
    sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('tracking_session_id', sessionId);
  }
  return sessionId;
};

// Récupération des paramètres UTM depuis l'URL
const getUTMParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  // Lien d'e-mail arrivé sur une page privée non suivie (éditeur de mini-site) : ses paramètres
  // utm_* mis de côté sont attachés à la première page publique vue ensuite (trackerLoader.js).
  if (!urlParams.get('utm_source')) {
    const pending = takePendingUtm();
    if (pending) return pending;
  }
  return {
    utm_source: urlParams.get('utm_source') || '',
    utm_medium: urlParams.get('utm_medium') || '',
    utm_campaign: urlParams.get('utm_campaign') || '',
    utm_content: urlParams.get('utm_content') || '',
    utm_term: urlParams.get('utm_term') || ''
  };
};

// Détection du type d'appareil
const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

// Détection du navigateur
const getBrowser = () => {
  const ua = navigator.userAgent;
  if (ua.indexOf('Firefox') > -1) return 'Firefox';
  if (ua.indexOf('SamsungBrowser') > -1) return 'Samsung Internet';
  if (ua.indexOf('Opera') > -1 || ua.indexOf('OPR') > -1) return 'Opera';
  if (ua.indexOf('Trident') > -1) return 'IE';
  if (ua.indexOf('Edge') > -1) return 'Edge';
  if (ua.indexOf('Chrome') > -1) return 'Chrome';
  if (ua.indexOf('Safari') > -1) return 'Safari';
  return 'Unknown';
};

// Tracker une visite de page
export const trackPageView = async (path, title) => {
  try {
    const sessionId = getSessionId();
    const utmParams = getUTMParams();
    
    const data = {
      session_id: sessionId,
      page_url: path,
      page_title: title || document.title,
      referer: document.referrer,
      ...utmParams,
      device_type: getDeviceType(),
      browser: getBrowser()
    };
    
    await axios.post(
      `${API_URL}?cleApi=${CLE_API}&service=trackVisit`,
      data,
      { headers: { 'Content-Type': 'application/json' } }
    );
  } catch (error) {
    console.error('Erreur tracking visite:', error);
  }
};

// Tracker un événement
export const trackEvent = async (eventType, eventData = {}) => {
  // Relais vers les traceurs actifs (pixel Meta, GA4, PostHog...) — ne lève jamais d'erreur.
  relayEvent(eventType, eventData);
  try {
    const sessionId = getSessionId();
    
    const data = {
      session_id: sessionId,
      event_type: eventType,
      event_category: eventData.category || '',
      event_label: eventData.label || '',
      event_value: eventData.value || '',
      page_url: window.location.pathname,
      product_id: eventData.product_id || null,
      order_id: eventData.order_id || null
    };
    
    await axios.post(
      `${API_URL}?cleApi=${CLE_API}&service=trackEvent`,
      data,
      { headers: { 'Content-Type': 'application/json' } }
    );
  } catch (error) {
    console.error('Erreur tracking événement:', error);
  }
};

// Composant Hook pour le tracking automatique
export const useTracking = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Ne pas tracker les pages admin
    const excludedPaths = ['/gestion-commandes', '/gestion-catalogue', '/bibliotheque-visuels', '/gestion-promos', '/list-cards', '/creer-card', '/edit-card', '/analytics', '/personnaliser-mini-site', '/apercu-mini-site', '/trackers', '/tunnel-acquisition'];
    const isExcluded = excludedPaths.some(path => location.pathname.startsWith(path));
    
    if (!isExcluded) {
      trackPageView(location.pathname + location.search);
    }
  }, [location]);
};

// Composant TrackingProvider à ajouter dans App.js
export default function TrackingProvider() {
  useTracking();
  return null;
}
